import { Fragment, useEffect, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  LuArrowRightLeft,
  LuExternalLink,
  LuGraduationCap,
  // LuMessageSquare,
  LuPanelLeftClose,
  LuPanelLeftOpen,
} from 'react-icons/lu';
import { Box } from '@/components/ui/lib/box';
import { HStack } from '@/components/ui/lib/hstack';
import { Button } from '@/components/ui/lib/button';
import { useDisclosure } from '@/components/ui/lib/use-disclosure';
import { IconButton } from '@/components/ui/lib/icon-button';
import { Image } from '@/components/ui/lib/image';
import { Text } from '@/components/ui/lib/text';
import { Separator } from '@/components/ui/lib/separator';
import { Tooltip } from '@/components/ui/lib/tooltip';
import WorkspaceAvatar from '@/components/ui/workspace-avatar';
import AuthConsoleControl from '@/app/components/auth-console-control';
import ColorModeToggleButton from '@/app/components/color-mode-toggle-button';
import WorkspaceSwitcherDialog from '@/app/components/workspace-switcher-dialog';
import AppLauncher from '@/app/components/app-launcher';
import { DASHBOARD_NAV_ITEMS } from '@/config/routes/dashboard-nav-items';
import {
  isDashboardNavPathAccessible,
  type DashboardNavPath,
} from '@/config/routes/dashboard-paths';
import { DICTIONARY } from '@/config/dictionary';
import { env } from '@/config/env';
import { useOverview } from '@/features/wholesaler-setup/api/overview-api';
import { useAppStore } from '@/stores/app-store';
import { cn } from '@/utils/cn';
import { ensureValidPath } from '@/utils/validate-path';
import '@/app/components/dashboard-nav.css';

const NAV_AUTO_COLLAPSE_MEDIA_QUERY = '(max-width: 1023px)';
const OVERVIEW_LOGO_STALE_MS = 5 * 60 * 1000;

const DashboardNav = () => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const currentUser = useAppStore((s) => s.currentUser);
  const wholesalers = useAppStore((s) => s.currentUser?.wholesalers) ?? [];
  const selectedWholesalerId = useAppStore((s) => s.selectedWholesalerId);
  const navCollapsed = useAppStore((s) => s.navCollapsed);
  const toggleNavCollapsed = useAppStore((s) => s.toggleNavCollapsed);
  const colorMode = useAppStore((s) => s.colorMode);
  const logoSrc =
    colorMode === 'dark' && env.VITE_LOGO_URL_DARK ? env.VITE_LOGO_URL_DARK : env.VITE_LOGO_URL;
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(NAV_AUTO_COLLAPSE_MEDIA_QUERY).matches;
  });
  const [narrowNavCollapsed, setNarrowNavCollapsed] = useState<boolean | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.matchMedia(NAV_AUTO_COLLAPSE_MEDIA_QUERY).matches ? true : null;
  });
  const { open: switcherOpen, onOpen: openSwitcher, onClose: closeSwitcher } = useDisclosure();
  // Feedback is planned work, deliberately left switched off — DO NOT delete this or the
  // src/features/feedback module. Re-enable by uncommenting this line, the trigger's
  // onClick below, and the <FeedbackModal> render at the bottom of this file, then remove
  // the feedback ignore entry from knip.jsonc. See feedback-modal.tsx for the full note.
  // const { open: feedbackOpen, onOpen: openFeedback, onClose: closeFeedback } = useDisclosure();
  const switcherCopy = DICTIONARY.dashboard.workspaceSwitcher;
  const selectedWholesaler =
    wholesalers.find((wholesaler) => wholesaler.id === selectedWholesalerId) ??
    wholesalers[0] ??
    null;
  // GET /overview is the only read path that exposes the stored logo; the stale window keeps
  // the nav to one fetch per workspace instead of one per mount.
  const { data: overview } = useOverview(selectedWholesaler?.id ?? null, {
    staleTime: OVERVIEW_LOGO_STALE_MS,
    refetchOnMount: false,
  });
  const effectiveNavCollapsed = isAutoCollapsed ? (narrowNavCollapsed ?? true) : navCollapsed;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(NAV_AUTO_COLLAPSE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsAutoCollapsed(event.matches);

      if (event.matches) {
        setNarrowNavCollapsed(true);
        return;
      }

      setNarrowNavCollapsed(null);
    };

    setIsAutoCollapsed(mediaQuery.matches);
    setNarrowNavCollapsed(mediaQuery.matches ? true : null);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const handleToggleNavCollapsed = () => {
    if (isAutoCollapsed) {
      setNarrowNavCollapsed((currentValue) => !(currentValue ?? true));
      return;
    }

    toggleNavCollapsed();
  };

  const isActive = (to: DashboardNavPath) => pathname === to || pathname.startsWith(`${to}/`);
  const visibleNavItems = DASHBOARD_NAV_ITEMS.filter((item) =>
    isDashboardNavPathAccessible(item.to, currentUser, selectedWholesalerId),
  );

  const collapseLabel = effectiveNavCollapsed
    ? DICTIONARY.dashboard.expandNav
    : DICTIONARY.dashboard.collapseNav;
  const openPortal = () => {
    window.open(env.VITE_TCAP_PORTAL_URL, '_blank', 'noopener,noreferrer');
  };
  const openDemos = () => {
    window.open('https://demos.pingco.cloud/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      as="aside"
      w={effectiveNavCollapsed ? '20' : '64'}
      flexShrink="0"
      alignSelf="stretch"
      position="sticky"
      top="0"
      zIndex="20"
      className={cn('dashboard-nav', effectiveNavCollapsed && 'dashboard-nav--collapsed')}
    >
      {/* Brand header */}
      <Box className="dashboard-nav__header">
        {effectiveNavCollapsed ? (
          <Box className="dashboard-nav__collapsed-header">
            <Tooltip content={collapseLabel} positioning={{ placement: 'right' }} openDelay={300}>
              <IconButton
                aria-label={collapseLabel}
                variant="ghost"
                size="sm"
                className="text-muted hover:bg-subtle hover:text-base"
                onClick={handleToggleNavCollapsed}
              >
                <LuPanelLeftOpen className="h-4 w-4" />
              </IconButton>
            </Tooltip>
            <AppLauncher />
            {selectedWholesaler && (
              <Tooltip
                content={DICTIONARY.dashboard.changeWorkspaceTooltip}
                positioning={{ placement: 'right' }}
                openDelay={300}
              >
                <Button
                  type="button"
                  variant="ghost"
                  w="auto"
                  h="auto"
                  p="0"
                  onClick={openSwitcher}
                >
                  <WorkspaceAvatar
                    name={selectedWholesaler.name}
                    logoUrl={overview?.logo}
                    size="md"
                  />
                </Button>
              </Tooltip>
            )}
          </Box>
        ) : (
          <>
            <Box className="dashboard-nav__brand-row">
              <Image
                src={logoSrc}
                alt={DICTIONARY.brand.full}
                className="dashboard-nav__brand-logo"
              />
              <HStack gap="1">
                <AppLauncher />
                <Tooltip
                  content={collapseLabel}
                  positioning={{ placement: 'right' }}
                  openDelay={300}
                >
                  <IconButton
                    aria-label={collapseLabel}
                    variant="ghost"
                    size="sm"
                    className="text-muted hover:bg-subtle hover:text-base"
                    onClick={handleToggleNavCollapsed}
                  >
                    <LuPanelLeftClose className="h-4 w-4" />
                  </IconButton>
                </Tooltip>
              </HStack>
            </Box>

            <Separator className="dashboard-nav__divider border-rim" />

            {selectedWholesaler && (
              <Box as="button" className="dashboard-nav__workspace-btn" onClick={openSwitcher}>
                <Box className="dashboard-nav__workspace-inner">
                  <WorkspaceAvatar
                    name={selectedWholesaler.name}
                    logoUrl={overview?.logo}
                    size="sm"
                  />
                  <Box className="dashboard-nav__workspace-info">
                    <Text as="p" className="dashboard-nav__workspace-label text-muted">
                      {switcherCopy.summaryLabel}
                    </Text>
                    <Text as="p" className="dashboard-nav__workspace-name text-base">
                      {selectedWholesaler.name}
                    </Text>
                  </Box>
                  <LuArrowRightLeft className="h-3.5 w-3.5 text-muted" />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box px="2">
        <Separator className="border-rim" />
      </Box>

      {/* Nav links */}
      <Box
        className={cn(
          'dashboard-nav__links',
          effectiveNavCollapsed && 'dashboard-nav__links--collapsed',
        )}
      >
        <Box className="dashboard-nav__links-list">
          {visibleNavItems.map((item) => {
            const active = isActive(item.to);
            const Icon = item.icon;

            return (
              <Fragment key={item.to}>
                {/* {item.to === APP_PATHS.adminSettings && (
                  <Tooltip
                    content={DICTIONARY.feedbackModal.triggerLabel}
                    positioning={{ placement: 'right' }}
                    openDelay={300}
                    disabled={!effectiveNavCollapsed}
                  >
                    <Button
                      variant="ghost"
                      w="full"
                      justifyContent={effectiveNavCollapsed ? 'center' : 'flex-start'}
                      className={cn(
                        'dashboard-nav-link dashboard-nav-link--inactive',
                        effectiveNavCollapsed ? 'dashboard-nav-link--collapsed' : '',
                      )}
                      // onClick={openFeedback}
                    >
                      <LuMessageSquare className="dashboard-nav-icon dashboard-nav-icon--inactive" />
                      {!effectiveNavCollapsed && (
                        <Text as="span" className="dashboard-nav-label">
                          {DICTIONARY.feedbackModal.triggerLabel}
                        </Text>
                      )}
                    </Button>
                  </Tooltip>
                )} */}
                <Tooltip
                  content={item.label}
                  positioning={{ placement: 'right' }}
                  openDelay={300}
                  disabled={!effectiveNavCollapsed}
                >
                  <Link
                    to={ensureValidPath(item.to)}
                    className={cn(
                      'dashboard-nav-link',
                      active ? 'dashboard-nav-link--active' : 'dashboard-nav-link--inactive',
                      effectiveNavCollapsed ? 'dashboard-nav-link--collapsed' : '',
                    )}
                  >
                    <Icon
                      className={cn(
                        'dashboard-nav-icon',
                        active ? 'dashboard-nav-icon--active' : 'dashboard-nav-icon--inactive',
                      )}
                    />
                    {!effectiveNavCollapsed && (
                      <Text as="span" className="dashboard-nav-label">
                        {item.label}
                      </Text>
                    )}
                  </Link>
                </Tooltip>
              </Fragment>
            );
          })}

          {wholesalers.length > 0 && (
            <>
              <Tooltip
                content={DICTIONARY.auth.userMenu.tcapPortalLabel}
                positioning={{ placement: 'right' }}
                openDelay={300}
                disabled={!effectiveNavCollapsed}
              >
                <Button
                  variant="ghost"
                  w="full"
                  className={cn(
                    'dashboard-nav-link dashboard-nav-link--portal',
                    effectiveNavCollapsed ? 'dashboard-nav-link--collapsed' : '',
                  )}
                  onClick={openPortal}
                >
                  <LuExternalLink className="dashboard-nav-icon dashboard-nav-icon--portal" />
                  {!effectiveNavCollapsed && (
                    <Text as="span" className="dashboard-nav-label">
                      {DICTIONARY.auth.userMenu.tcapPortalLabel}
                    </Text>
                  )}
                </Button>
              </Tooltip>

              <Tooltip
                content={DICTIONARY.auth.userMenu.tcapDemosLabel}
                positioning={{ placement: 'right' }}
                openDelay={300}
                disabled={!effectiveNavCollapsed}
              >
                <Button
                  variant="ghost"
                  w="full"
                  className={cn(
                    'dashboard-nav-link dashboard-nav-link--portal',
                    effectiveNavCollapsed ? 'dashboard-nav-link--collapsed' : '',
                  )}
                  onClick={openDemos}
                >
                  <LuGraduationCap className="dashboard-nav-icon dashboard-nav-icon--portal" />
                  {!effectiveNavCollapsed && (
                    <Text as="span" className="dashboard-nav-label">
                      {DICTIONARY.auth.userMenu.tcapDemosLabel}
                    </Text>
                  )}
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Box px="2">
        <Separator className="border-rim" />
      </Box>

      {/* Footer controls */}
      <Box className="dashboard-nav__footer">
        <Box
          className={cn(
            'dashboard-nav__footer-controls',
            effectiveNavCollapsed && 'dashboard-nav__footer-controls--collapsed',
          )}
        >
          <ColorModeToggleButton />
          <AuthConsoleControl />
        </Box>
        {!effectiveNavCollapsed && (
          <Box className="dashboard-nav__footer-meta">
            <Text px="2" fontSize="xs" className="text-muted">
              {DICTIONARY.footer.copyright(new Date().getFullYear())}
            </Text>
            <Text px="2" fontSize="xs" className="text-muted">
              v{DICTIONARY.brand.version}
            </Text>
          </Box>
        )}
      </Box>

      <WorkspaceSwitcherDialog open={switcherOpen} onClose={closeSwitcher} />
      {/* <FeedbackModal open={feedbackOpen} onClose={closeFeedback} /> */}
    </Box>
  );
};

export default DashboardNav;
